import BaseController from "./BaseController";
import { ActionType } from "../util/CommonConstant";
import { FormState } from "../interface/common";
import {
  PageUploadPDFModel,
  AudioWorkflowRow,
  AudioWorkflowsPagination,
} from "../model/PageUploadPDFModel";
import PageUploadAudioAPIService from "../service/PageUploadAudioAPIService";
import { formattedDate } from "../util/CommonUtil";

class PageUploadAudioController extends BaseController {
  private state: FormState<PageUploadPDFModel>;
  private apiService = PageUploadAudioAPIService;

  constructor(state: FormState<PageUploadPDFModel>, dispatch: any) {
    super(dispatch);
    this.state = state;
  }

  handleFileChange = (file: File | null) => {
    this.dispatch({
      type: ActionType.SET_SELECTED_FILE,
      payload: file,
    });
  };

  handleFilesChange = (files: File[]) => {
    this.dispatch({
      type: ActionType.SET_SELECTED_FILES,
      payload: files,
    });
  };

  setUploadLogs = (logs: { timestamp: string; message: string }[] | null) => {
    this.dispatch({
      type: ActionType.SET_UPLOAD_LOGS,
      payload: logs,
    });
  };

  setSimulationLogs = (logs: { timestamp: string; message: string }[] | null) => {
    this.dispatch({
      type: ActionType.SET_SIMULATION_LOGS,
      payload: logs,
    });
  };

  setIsUploading = (isUploading: boolean) => {
    this.dispatch({
      type: ActionType.SET_IS_UPLOADING,
      payload: isUploading,
    });
  };

  setFileUploadUUID = (uuid: string) => {
    this.dispatch({
      type: ActionType.SET_FILE_UPLOAD_UUID,
      payload: uuid,
    });
  };

  setIsProcessCompleted = (isCompleted: boolean) => {
    this.dispatch({
      type: ActionType.SET_IS_PROCESS_COMPLETED,
      payload: isCompleted,
    });
  };

  setCompletedPdfName = (pdfName: string) => {
    this.dispatch({
      type: ActionType.SET_COMPLETED_PDF_NAME,
      payload: pdfName,
    });
  };

  setCompletedPdfNames = (pdfNames: string[]) => {
    this.dispatch({
      type: ActionType.SET_COMPLETED_PDF_NAMES,
      payload: pdfNames,
    });
  };

  setProcessingLogs = (logs: Array<{ timestamp: string; message: string; level: string }>) => {
    this.dispatch({
      type: ActionType.SET_PROCESSING_LOGS,
      payload: logs,
    });
  };

  setSimulation = (simulation: boolean) => {
    this.dispatch({
      type: ActionType.SET_SIMULATION,
      payload: simulation,
    });
  };

  setAudioWorkflows = (workflows: AudioWorkflowRow[]) => {
    this.dispatch({
      type: ActionType.SET_AUDIO_WORKFLOWS,
      payload: workflows,
    });
  };

  setAudioWorkflowsLoading = (loading: boolean) => {
    this.dispatch({
      type: ActionType.SET_AUDIO_WORKFLOWS_LOADING,
      payload: loading,
    });
  };

  setAudioWorkflowsError = (error: string | null) => {
    this.dispatch({
      type: ActionType.SET_AUDIO_WORKFLOWS_ERROR,
      payload: error,
    });
  };

  setAudioWorkflowsPagination = (pagination: AudioWorkflowsPagination) => {
    this.dispatch({
      type: ActionType.SET_AUDIO_WORKFLOWS_PAGINATION,
      payload: pagination,
    });
  };

  setShowUploadModal = (show: boolean) => {
    this.dispatch({
      type: ActionType.SET_SHOW_UPLOAD_MODAL,
      payload: show,
    });
  };

  handleUpload = async (file: File): Promise<any> => {
    try {
      const response = await this.apiService.uploadAudio(file, this.dispatch);
      if (response?.data && response.data.pdf_name) {
        this.setFileUploadUUID(response.data.pdf_name);
        return response;
      }
    } catch (error) {
      throw error;
    }
  };

  handleUploadAndAnalyze = async (file: File): Promise<any> => {
    return this.handleUploadAndAnalyzeMultiple([file]);
  };

  handleUploadAndAnalyzeMultiple = async (
    files: File[],
    predefinedAudioNames?: string[]
  ): Promise<any> => {
    try {
      const response = await this.apiService.uploadAndAnalyzeMultiple(
        files,
        this.dispatch,
        predefinedAudioNames
      );
      if (response?.data && response.data.job_id) {
        this.setFileUploadUUID(response.data.job_id);
        return response;
      }
    } catch (error) {
      throw error;
    }
  };

  private pollIntervalId: NodeJS.Timeout | null = null;

  startStatusPolling = (): void => {
    if (!this.state.data.fileUploadUUID) {
      return;
    }

    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
    }

    const audioName = this.state.data.fileUploadUUID;

    this.pollIntervalId = setInterval(async () => {
      try {
        const statusData = await this.apiService.getStatus(audioName);

        if (statusData && statusData.status === "completed") {
          this.setIsProcessCompleted(true);
          if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
          }
        } else if (statusData && statusData.status === "error") {
          this.setIsProcessCompleted(true);
          if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
          }
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401) {
          return;
        }
        if (this.pollIntervalId) {
          clearInterval(this.pollIntervalId);
          this.pollIntervalId = null;
        }
      }
    }, 2000);

    setTimeout(() => {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }
    }, 5 * 60 * 1000);
  };

  startJobPolling = (onComplete: (result: any) => void): void => {
    if (!this.state.data.fileUploadUUID) {
      return;
    }

    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
    }

    const jobId = this.state.data.fileUploadUUID;

    this.pollIntervalId = setInterval(async () => {
      try {
        const jobData = await this.apiService.getJobResult(jobId);

        if (jobData && jobData.logs && Array.isArray(jobData.logs)) {
          this.setProcessingLogs(jobData.logs);
        }

        if (jobData && jobData.status === "completed") {
          this.setIsProcessCompleted(true);
          if (jobData.pdf_names && Array.isArray(jobData.pdf_names)) {
            this.setCompletedPdfNames(jobData.pdf_names);
            if (jobData.pdf_names.length > 0) {
              this.setCompletedPdfName(jobData.pdf_names[0]);
            }
          } else if (jobData.pdf_name) {
            this.setCompletedPdfName(jobData.pdf_name);
            this.setCompletedPdfNames([jobData.pdf_name]);
          }
          if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
          }
          onComplete(jobData);
        } else if (jobData && jobData.status === "error") {
          this.setIsProcessCompleted(true);
          if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
          }
          onComplete(jobData);
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401) {
          return;
        }
        if (this.pollIntervalId) {
          clearInterval(this.pollIntervalId);
          this.pollIntervalId = null;
        }
      }
    }, 2000);

    setTimeout(() => {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }
    }, 10 * 60 * 1000);
  };

  /** Stops the `/job/{id}` polling interval (multi-file upload flow). */
  stopJobPolling = (): void => {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  };

  /**
   * Fetches a page of triggered workflows for the landing list. Resolves to a
   * result describing the outcome so the poller can decide whether to continue.
   * Never rejects.
   */
  fetchWorkflows = async (pageOverride?: number): Promise<{ ok: boolean; retryable: boolean }> => {
    try {
      this.setAudioWorkflowsLoading(true);
      this.setAudioWorkflowsError(null);

      const current = this.state.data.audioWorkflowsPagination;
      const page = pageOverride ?? current.page;
      const { statuses, pagination } = await this.apiService.listWorkflowStatuses(
        page,
        current.pageSize
      );

      // A row without a file url cannot be tracked via `/audio/progress`, so drop it.
      const rows: AudioWorkflowRow[] = statuses
        .filter((record) => record.fileUrl)
        .map((record) => ({
          id: record.id || record.fileUrl || "",
          fileUrl: record.fileUrl || "",
          steps: record.steps || {},
          createdAt: record.createdAt,
          updatedAt: record.updatedAt,
        }));

      this.setAudioWorkflows(rows);
      this.setAudioWorkflowsPagination({
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalCount: pagination.totalCount,
        hasNext: pagination.hasNext,
      });
      return { ok: true, retryable: false };
    } catch (error: any) {
      console.error("Error fetching workflows:", error);
      this.setAudioWorkflowsError(error?.message || "Failed to load workflows");
      return { ok: false, retryable: this._isRetryableError(error) };
    } finally {
      this.setAudioWorkflowsLoading(false);
    }
  };

  /**
   * Determines whether a failed request is worth retrying. Transient
   * conditions (network errors, timeouts, rate limiting, auth refresh,
   * server errors) are retryable; deterministic client errors are not.
   */
  private _isRetryableError = (error: any): boolean => {
    const status = error?.response?.status;
    // No response at all => network/timeout error => retryable
    if (status === undefined) {
      return true;
    }
    // 401 (token may be refreshed), 408 timeout, 429 rate limit, and any 5xx
    if (status === 401 || status === 408 || status === 429 || status >= 500) {
      return true;
    }
    // Other 4xx are deterministic client errors => not retryable
    return false;
  };

  private workflowsListPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private workflowsListPollStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private workflowsListPollingActive = false;
  private workflowsListPollInFlight = false;
  private workflowsListVisibilityHandler: (() => void) | null = null;
  private static readonly WORKFLOWS_LIST_POLL_INTERVAL = 10000; // 10s between successful cycles
  private static readonly WORKFLOWS_LIST_POLL_RETRY_DELAY = 3000; // shorter backoff after a retryable error

  startWorkflowsListPolling = (): void => {
    this.stopWorkflowsListPolling();

    this.workflowsListPollingActive = true;

    // Kick off the self-chaining loop. The next tick is scheduled only after
    // the previous request settles, so cycles never overlap.
    this._scheduleNextWorkflowsListPoll(PageUploadAudioController.WORKFLOWS_LIST_POLL_INTERVAL);

    // Safety timeout: stop polling after 30 minutes.
    this.workflowsListPollStopTimeoutId = setTimeout(() => {
      this.stopWorkflowsListPolling();
    }, 30 * 60 * 1000);

    // When the tab becomes visible again, catch up immediately if the loop
    // has no pending tick (e.g. cleared by browser throttling).
    this.workflowsListVisibilityHandler = () => {
      if (!this.workflowsListPollingActive || document.hidden) return;
      if (!this.workflowsListPollTimeoutId && !this.workflowsListPollInFlight) {
        this._scheduleNextWorkflowsListPoll(0);
      }
    };
    document.addEventListener("visibilitychange", this.workflowsListVisibilityHandler);
  };

  /**
   * Schedules a single poll cycle `delay` ms from now. Each cycle awaits
   * fetchWorkflows; a success schedules the next cycle at the normal interval,
   * a retryable error reschedules sooner, and a non-retryable error stops the loop.
   */
  private _scheduleNextWorkflowsListPoll = (delay: number): void => {
    if (!this.workflowsListPollingActive) {
      return;
    }

    this.workflowsListPollTimeoutId = setTimeout(async () => {
      this.workflowsListPollTimeoutId = null;
      if (!this.workflowsListPollingActive) {
        return;
      }

      this.workflowsListPollInFlight = true;
      const result = await this.fetchWorkflows();
      this.workflowsListPollInFlight = false;

      if (!this.workflowsListPollingActive) {
        return;
      }

      if (result.ok) {
        this._scheduleNextWorkflowsListPoll(PageUploadAudioController.WORKFLOWS_LIST_POLL_INTERVAL);
      } else if (result.retryable) {
        console.warn("Workflows list poll failed with a retryable error; retrying.");
        this._scheduleNextWorkflowsListPoll(
          PageUploadAudioController.WORKFLOWS_LIST_POLL_RETRY_DELAY
        );
      } else {
        console.error("Workflows list poll failed with a non-retryable error; stopping.");
        this.stopWorkflowsListPolling();
      }
    }, delay);
  };

  stopWorkflowsListPolling = (): void => {
    this.workflowsListPollingActive = false;

    if (this.workflowsListPollTimeoutId) {
      clearTimeout(this.workflowsListPollTimeoutId);
      this.workflowsListPollTimeoutId = null;
    }

    if (this.workflowsListPollStopTimeoutId) {
      clearTimeout(this.workflowsListPollStopTimeoutId);
      this.workflowsListPollStopTimeoutId = null;
    }

    if (this.workflowsListVisibilityHandler) {
      document.removeEventListener("visibilitychange", this.workflowsListVisibilityHandler);
      this.workflowsListVisibilityHandler = null;
    }
  };

  handlePageChange = (page: number) => {
    this.setAudioWorkflowsPagination({
      ...this.state.data.audioWorkflowsPagination,
      page,
    });
    // Pass the page explicitly: `this.state` is only refreshed on the next render.
    this.fetchWorkflows(page);
  };

  startSimulation = (uploadLogs: { timestamp: string; message: string }[]): (() => void) => {
    const TARGET_DURATION = 40000;
    let startTime = Date.now();
    let timeoutId: NodeJS.Timeout;

    const scheduleNextBatch = () => {
      const currentLogs = this.state.data.simulationLogs || [];
      const currentLength = currentLogs.length;
      const totalLogs = uploadLogs.length;
      const elapsedTime = Date.now() - startTime;
      const remainingTime = TARGET_DURATION - elapsedTime;
      const remainingLogs = totalLogs - currentLength;

      if (remainingTime <= 2000 || remainingLogs <= 0) {
        const newLogs = uploadLogs
          .map((log: { message: string }) => ({
            timestamp: formattedDate(new Date().toISOString()),
            message: log.message,
          }))
          .slice(currentLength);

        const updatedLogs = currentLogs.length > 0 ? [...currentLogs, ...newLogs] : newLogs;
        this.setSimulationLogs(updatedLogs);
        this.setIsProcessCompleted(true);
        return;
      }

      let batchSize: number;
      let nextInterval: number;

      if (currentLength < 6 && currentLength < totalLogs) {
        batchSize = 1;
        nextInterval = Math.random() * 500 + 1000;
      } else if (remainingLogs <= 5 || remainingTime <= 10000) {
        batchSize = 1;
        nextInterval = Math.random() * 500 + 1000;
      } else {
        const middleLogs = totalLogs - 6 - 5;
        const currentMiddlePosition = currentLength - 6;
        const remainingMiddleLogs = middleLogs - currentMiddlePosition;
        const remainingMiddleTime = remainingTime - 10000;
        const estimatedMiddleBatches = Math.ceil(remainingMiddleTime / 300);
        batchSize = Math.max(1, Math.min(2, Math.ceil(remainingMiddleLogs / estimatedMiddleBatches)));
        batchSize = Math.min(batchSize, remainingLogs - 5);
        nextInterval = Math.random() * 300 + 200;
      }

      batchSize = Math.max(1, Math.min(batchSize, remainingLogs));

      const newLogs = uploadLogs
        .map((log: { message: string }) => ({
          timestamp: formattedDate(new Date().toISOString()),
          message: log.message,
        }))
        .slice(currentLength, currentLength + batchSize);

      const updatedLogs = currentLogs.length > 0 ? [...currentLogs, ...newLogs] : newLogs;

      if (updatedLogs.length >= uploadLogs.length) {
        this.setSimulationLogs(updatedLogs);
        this.setIsProcessCompleted(true);
        return;
      }

      this.setSimulationLogs(updatedLogs);
      timeoutId = setTimeout(scheduleNextBatch, nextInterval);
    };

    timeoutId = setTimeout(scheduleNextBatch, 1000);

    const safetyTimeout = setTimeout(() => {
      this.setIsProcessCompleted(true);
      clearTimeout(timeoutId);
    }, TARGET_DURATION);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(safetyTimeout);
    };
  };
}

export default PageUploadAudioController;
